import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CardFooter } from "@/components/ui/card";
import { format } from "date-fns";

interface InnoventureTestActionsProps {
  testId: string;
  shareToken: string;
  hasAlreadyTaken: boolean;
  canResume?: boolean;
  resumeFromQuestionIndex?: number;
  isScheduledTest: boolean;
  hasStarted: boolean;
  hasEnded: boolean;
  scheduledStartTime?: string;
  onBack: () => void;
  accentColor: string;
}

export function InnoventureTestActions({
  shareToken,
  hasAlreadyTaken,
  canResume = false,
  resumeFromQuestionIndex = 0,
  isScheduledTest,
  hasStarted,
  hasEnded,
  scheduledStartTime,
  onBack,
  accentColor,
}: InnoventureTestActionsProps) {
  const navigate = useNavigate();

  const handleStartTest = () => {
    navigate(`/test-start/${shareToken}`);
  };

  const getButtonState = () => {
    if (hasAlreadyTaken) {
      return {
        disabled: true,
        text: 'Already Completed',
        showBadge: true,
      };
    }
    
    if (canResume) {
      return {
        disabled: false,
        text: `Resume Test (Question ${resumeFromQuestionIndex + 1})`,
        showBadge: false,
      };
    }

    if (isScheduledTest && !hasStarted && scheduledStartTime) {
      return {
        disabled: true,
        text: `Test starts on ${format(new Date(scheduledStartTime), 'MMM d, yyyy h:mm a')}`,
        showBadge: false,
      };
    }

    if (isScheduledTest && hasEnded) {
      return {
        disabled: true,
        text: 'Test has ended',
        showBadge: false,
      };
    }

    return {
      disabled: false,
      text: 'Start Test',
      showBadge: false,
    };
  };

  const buttonState = getButtonState();

  return (
    <CardFooter className="flex flex-col gap-4 p-8 pt-0">
      {buttonState.showBadge ? (
        <Badge variant="outline" className="w-fit px-4 py-2 font-medium">
          Already Completed
        </Badge>
      ) : (
        <Button
          onClick={handleStartTest}
          disabled={buttonState.disabled}
          className="w-full font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          size="lg"
          style={!buttonState.disabled ? { backgroundColor: accentColor } : undefined}
        >
          {buttonState.text}
        </Button>
      )}

      <Button
        onClick={onBack}
        variant="outline"
        className="w-full"
        size="lg"
      >
        Back to Tests
      </Button>
    </CardFooter>
  );
}
